<script>
import { preventDefault } from "svelte/legacy";

/**
 * Audio Embed Export Component
 *
 * Lets users embed an Audio Description audio track into an existing audio or video
 * file (e.g. MXF / MOV / MP4) using FFmpeg. Video and container metadata are copied
 * without transcoding; audio output is `-c:a copy` by default with optional PCM or
 * AAC re-encoding.
 *
 * This modal is desktop + AD plugin only; gating is enforced both by the entry card
 * in FileExport.svelte and by the mount block in Creator.svelte.
 */

import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { fade } from "svelte/transition";
import { toast } from "@zerodevx/svelte-toast";
import * as m from "@app/paraglide/messages.js";

import {
    probeTracks,
    audioTracksOf,
    passthroughTracksOf,
} from "@app/lib/ffmpegProbe.js";

/* Electron / FFMPEG */
const ffmpeg = window.ffmpeg;
const { shell, webUtils } = window;
const ipcRenderer = window.ipcRenderer;
const path = window.path;

let nextRowId = 0;
const newRowId = () => ++nextRowId;

const AUDIO_CODECS = ["copy", "pcm_s16le", "pcm_s24le", "aac"];

function codecLabel(value) {
    switch (value) {
        case "copy":
            return m.audio_embed_export_codec_copy();
        case "pcm_s16le":
            return m.audio_embed_export_codec_pcm_s16le();
        case "pcm_s24le":
            return m.audio_embed_export_codec_pcm_s24le();
        case "aac":
            return m.audio_embed_export_codec_aac();
        default:
            return value;
    }
}

let originalFilePath = $state(null),
    adFilePath = $state(null),
    targetFolderPath = $state(sessionStorage.getItem("audioEmbedExportPath") || null),
    originalProbe = $state(null),
    adProbe = $state(null),
    scanning = $state(false),
    scanError = $state(""),
    outputRows = $state([]),
    audioCodecMode = $state("copy"),
    filenameSuffix = $state("_ADembed"),
    exporting = $state(false),
    progress = $state(0),
    statusMsg = $state(""),
    exportCompleted = $state(false),
    lastOutputPath = $state("");

let scanReady = $derived(Boolean(originalFilePath && adFilePath));
let scanCompleted = $derived(Boolean(originalProbe && adProbe));

let availableAudioSources = $derived.by(() => {
    const sources = [];
    if (originalProbe) {
        for (const t of audioTracksOf(originalProbe)) {
            sources.push({
                sourceInput: "original",
                streamIdx: t.streamIdx,
                label: m.audio_embed_export_source_option_original({ label: t.label }),
                meta: t,
            });
        }
    }
    if (adProbe) {
        for (const t of audioTracksOf(adProbe)) {
            sources.push({
                sourceInput: "ad",
                streamIdx: t.streamIdx,
                label: m.audio_embed_export_source_option_ad({ label: t.label }),
                meta: t,
            });
        }
    }
    return sources;
});

let canExport = $derived(Boolean(scanCompleted && targetFolderPath && !exporting));

let outputFileName = $derived.by(() => {
    if (!originalFilePath) return "";
    const ext = path.extname(originalFilePath);
    const base = path.basename(originalFilePath, ext);
    const suffix = (filenameSuffix || "").trim();
    return `${base}${suffix}${ext}`;
});

function sourceKey(row) {
    return `${row.sourceInput}:${row.streamIdx}`;
}

function findSourceByKey(key) {
    return availableAudioSources.find((s) => `${s.sourceInput}:${s.streamIdx}` === key);
}

function seedDefaultRows() {
    const rows = [];
    if (originalProbe) {
        for (const t of audioTracksOf(originalProbe)) {
            rows.push({
                id: newRowId(),
                sourceInput: "original",
                streamIdx: t.streamIdx,
                language: t.language || "",
            });
        }
    }
    if (adProbe) {
        for (const t of audioTracksOf(adProbe)) {
            rows.push({
                id: newRowId(),
                sourceInput: "ad",
                streamIdx: t.streamIdx,
                language: t.language || "ad",
            });
        }
    }
    outputRows = rows;
}

async function handleOriginalFileSelection(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
        originalFilePath = null;
        return;
    }
    try {
        originalFilePath = await webUtils.getPathForFile(files[0]);
        originalProbe = null;
        scanError = "";
        outputRows = [];
        exportCompleted = false;
    } catch (err) {
        console.error("Original media path lookup failed:", err);
        toast.push(m.audio_embed_export_select_original_failed(), { classes: ["toast-danger"] });
        originalFilePath = null;
    }
}

async function handleAdFileSelection(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
        adFilePath = null;
        return;
    }
    try {
        adFilePath = await webUtils.getPathForFile(files[0]);
        adProbe = null;
        scanError = "";
        outputRows = [];
        exportCompleted = false;
    } catch (err) {
        console.error("AD audio path lookup failed:", err);
        toast.push(m.audio_embed_export_select_ad_failed(), { classes: ["toast-danger"] });
        adFilePath = null;
    }
}

async function selectTargetFolder() {
    try {
        const result = await ipcRenderer.invoke("selectFolder", {
            title: m.audio_embed_export_select_folder_title(),
            properties: ["openDirectory", "createDirectory"],
        });
        if (result && !result.canceled && result.filePaths && result.filePaths[0]) {
            targetFolderPath = result.filePaths[0];
            sessionStorage.setItem("audioEmbedExportPath", targetFolderPath);
            exportCompleted = false;
        }
    } catch (err) {
        console.error("Folder selection failed:", err);
    }
}

async function scanTracks() {
    if (!scanReady || scanning) return;
    scanning = true;
    scanError = "";
    try {
        const [origRes, adRes] = await Promise.all([
            probeTracks(originalFilePath),
            probeTracks(adFilePath),
        ]);
        originalProbe = origRes;
        adProbe = adRes;
        seedDefaultRows();
        toast.push(
            m.audio_embed_export_scan_complete_toast({
                originalAudio: audioTracksOf(origRes).length,
                adAudio: audioTracksOf(adRes).length,
            }),
            { classes: ["toast-info"] }
        );
    } catch (err) {
        console.error("Scan failed:", err);
        const message = err?.message || String(err);
        scanError = message;
        toast.push(m.audio_embed_export_scan_failed({ error: message }), {
            classes: ["toast-danger"],
        });
    } finally {
        scanning = false;
    }
}

function moveRow(rowIndex, delta) {
    const targetIndex = rowIndex + delta;
    if (targetIndex < 0 || targetIndex >= outputRows.length) return;
    const next = outputRows.slice();
    const [moved] = next.splice(rowIndex, 1);
    next.splice(targetIndex, 0, moved);
    outputRows = next;
}

function removeRow(rowIndex) {
    outputRows = outputRows.filter((_, i) => i !== rowIndex);
}

function addRow() {
    const first = availableAudioSources[0];
    if (!first) return;
    outputRows = [
        ...outputRows,
        {
            id: newRowId(),
            sourceInput: first.sourceInput,
            streamIdx: first.streamIdx,
            language: first.meta?.language || "",
        },
    ];
}

function updateRowSource(rowIndex, key) {
    const src = findSourceByKey(key);
    if (!src) return;
    const next = outputRows.slice();
    next[rowIndex] = {
        ...next[rowIndex],
        sourceInput: src.sourceInput,
        streamIdx: src.streamIdx,
    };
    outputRows = next;
}

function resetRowsToDefault() {
    seedDefaultRows();
}

function buildOutputOptions() {
    const opts = [];
    if (originalProbe) {
        for (const stream of passthroughTracksOf(originalProbe)) {
            opts.push("-map", `0:${stream.streamIdx}`);
        }
    }
    outputRows.forEach((row, i) => {
        const inIdx = row.sourceInput === "original" ? 0 : 1;
        opts.push("-map", `${inIdx}:${row.streamIdx}`);
        const lang = (row.language || "").trim();
        if (lang) {
            opts.push(`-metadata:s:a:${i}`, `language=${lang}`);
        }
    });
    opts.push("-map_metadata", "0");
    opts.push("-c:v", "copy");
    opts.push("-c:s", "copy");
    opts.push("-c:d", "copy");
    opts.push("-c:a", audioCodecMode);
    opts.push("-y");
    return opts;
}

function close() {
    if (exporting) return;
    hideModal();
}

async function beginExport() {
    if (!canExport) return;
    if (!outputFileName) return;

    exporting = true;
    exportCompleted = false;
    progress = 0;
    statusMsg = m.audio_embed_export_progress_starting();
    lastOutputPath = "";

    const outPath = path.join(targetFolderPath, outputFileName);
    lastOutputPath = outPath;

    const outputOpts = buildOutputOptions();

    toast.push(m.audio_embed_export_starting_toast(), { classes: ["toast-info"] });

    const cmd = ffmpeg()
        .addInput(originalFilePath)
        .addInput(adFilePath)
        .outputOptions(outputOpts)
        .output(outPath)
        .on("start", (commandLine) => {
            console.log("[Audio Embed Export] FFmpeg command:", commandLine);
        })
        .on("progress", (p) => {
            if (typeof p.percent === "number" && !Number.isNaN(p.percent)) {
                progress = Math.max(0, Math.min(100, p.percent)).toFixed(2);
            }
            statusMsg = m.audio_embed_export_progress_running({
                timecode: p.timemark || "00:00:00",
            });
        })
        .on("stderr", (line) => {
            console.log("[ffmpeg]", line);
        })
        .on("end", () => {
            progress = 100;
            statusMsg = m.audio_embed_export_progress_complete();
            exporting = false;
            exportCompleted = true;
            toast.push(m.audio_embed_export_complete_toast(), { classes: ["toast-success"] });
            try {
                shell.openPath(targetFolderPath);
            } catch (err) {
                console.error("[Audio Embed Export] Could not open target folder:", err);
            }
        })
        .on("error", (err, stdout, stderr) => {
            console.error("FFmpeg failed:", err, stderr);
            exporting = false;
            exportCompleted = false;
            const message = err?.message || String(err);
            toast.push(m.audio_embed_export_failed_toast({ error: message }), {
                classes: ["toast-danger"],
            });
        });

    cmd.run();
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'audioEmbedExport' ? 'show d-block' : ''}"
    role="dialog"
    tabindex="-1"
    id="AudioEmbedExportModal"
    aria-labelledby="audioEmbedExportTitle"
    aria-describedby="audioEmbedExportDesc">
    <div
        class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 id="audioEmbedExportTitle" class="modal-title">
                    <i class="bi bi-soundwave"></i> {m.audio_embed_export_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.audio_embed_export_close_label()}
                    onclick={close}
                    disabled={exporting}
                    id="closeAudioEmbedExport"></button>
            </div>

            <div class="modal-body" id="audioEmbedExportDesc">
                <p class="text-muted small">{m.audio_embed_export_description()}</p>

                <form onsubmit={preventDefault(beginExport)}>
                    <!-- Inputs card -->
                    <div class="card mb-3 border-primary">
                        <div class="card-header bg-primary text-white">
                            <h5 class="mb-0">
                                <i class="bi bi-folder2-open"></i> {m.audio_embed_export_title()}
                            </h5>
                        </div>
                        <div class="card-body">
                            <!-- Original media -->
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="audioEmbedOriginalFile">
                                    <i class="bi bi-film"></i> {m.audio_embed_export_original_label()}
                                </label>
                                <div class="input-group">
                                    <label
                                        class="btn btn-outline-secondary text-dark mb-0"
                                        for="audioEmbedOriginalFile">
                                        <i class="bi bi-folder-open"></i> {m.audio_embed_export_original_browse()}
                                    </label>
                                    <input
                                        id="audioEmbedOriginalFile"
                                        type="file"
                                        class="d-none"
                                        accept="video/*,audio/*,.mxf,.mov,.mp4,.mkv,.m4v,.wav,.mp3,.aac,.flac"
                                        onchange={handleOriginalFileSelection}
                                        disabled={exporting} />
                                    <input
                                        type="text"
                                        class="form-control"
                                        value={originalFilePath || ""}
                                        placeholder={m.audio_embed_export_original_placeholder()}
                                        readonly />
                                </div>
                            </div>

                            <!-- AD track -->
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="audioEmbedAdFile">
                                    <i class="bi bi-mic"></i> {m.audio_embed_export_ad_label()}
                                </label>
                                <div class="input-group">
                                    <label
                                        class="btn btn-outline-secondary text-dark mb-0"
                                        for="audioEmbedAdFile">
                                        <i class="bi bi-folder-open"></i> {m.audio_embed_export_ad_browse()}
                                    </label>
                                    <input
                                        id="audioEmbedAdFile"
                                        type="file"
                                        class="d-none"
                                        accept="audio/*,.wav,.mp3,.aac,.flac,.m4a"
                                        onchange={handleAdFileSelection}
                                        disabled={exporting} />
                                    <input
                                        type="text"
                                        class="form-control"
                                        value={adFilePath || ""}
                                        placeholder={m.audio_embed_export_ad_placeholder()}
                                        readonly />
                                </div>
                            </div>

                            <!-- Target folder -->
                            <div class="mb-3">
                                <label class="form-label fw-bold" for="audioEmbedTargetFolder">
                                    <i class="bi bi-folder"></i> {m.audio_embed_export_target_label()}
                                </label>
                                <div class="input-group">
                                    <button
                                        id="audioEmbedTargetFolder"
                                        type="button"
                                        class="btn btn-outline-secondary text-dark"
                                        onclick={selectTargetFolder}
                                        disabled={exporting}>
                                        <i class="bi bi-folder-open"></i> {m.audio_embed_export_target_browse()}
                                    </button>
                                    <input
                                        type="text"
                                        class="form-control"
                                        value={targetFolderPath || ""}
                                        placeholder={m.audio_embed_export_target_placeholder()}
                                        readonly />
                                </div>
                            </div>

                            <!-- Scan -->
                            <div class="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    class="btn btn-secondary"
                                    onclick={scanTracks}
                                    disabled={!scanReady || scanning || exporting}>
                                    {#if scanning}
                                        <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        {m.audio_embed_export_scanning()}
                                    {:else}
                                        <i class="bi bi-search"></i> {m.audio_embed_export_scan()}
                                    {/if}
                                </button>
                                {#if !scanCompleted && !scanError}
                                    <small class="text-muted">
                                        <i class="bi bi-info-circle"></i> {m.audio_embed_export_scan_required()}
                                    </small>
                                {/if}
                                {#if scanError}
                                    <small class="text-danger">
                                        <i class="bi bi-exclamation-triangle"></i> {scanError}
                                    </small>
                                {/if}
                            </div>
                        </div>
                    </div>

                    {#if scanCompleted}
                        <!-- Detected streams summary -->
                        <div class="card mb-3" transition:fade={{ duration: 200 }}>
                            <div class="card-header bg-light">
                                <h6 class="mb-0">
                                    <i class="bi bi-list-ul"></i> {m.audio_embed_export_original_streams_heading()} / {m.audio_embed_export_ad_streams_heading()}
                                </h6>
                            </div>
                            <div class="card-body py-2">
                                <div class="row small">
                                    <div class="col-md-6">
                                        <strong>{m.audio_embed_export_original_streams_heading()}</strong>
                                        <ul class="list-unstyled mb-0">
                                            {#each originalProbe.tracks as track (track.streamIdx + "-" + track.type)}
                                                <li>
                                                    <span class="badge bg-secondary text-uppercase me-1">{track.type}</span>
                                                    {track.label}
                                                </li>
                                            {/each}
                                        </ul>
                                    </div>
                                    <div class="col-md-6">
                                        <strong>{m.audio_embed_export_ad_streams_heading()}</strong>
                                        <ul class="list-unstyled mb-0">
                                            {#each adProbe.tracks as track (track.streamIdx + "-" + track.type)}
                                                <li>
                                                    <span class="badge bg-secondary text-uppercase me-1">{track.type}</span>
                                                    {track.label}
                                                </li>
                                            {/each}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Output tracks -->
                        <div class="card mb-3" transition:fade={{ duration: 200 }}>
                            <div class="card-header bg-light d-flex justify-content-between align-items-center">
                                <h6 class="mb-0">
                                    <i class="bi bi-music-note-list"></i> {m.audio_embed_export_output_tracks()}
                                </h6>
                                <div class="d-flex gap-2">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary"
                                        onclick={resetRowsToDefault}
                                        disabled={exporting || availableAudioSources.length === 0}>
                                        <i class="bi bi-arrow-counterclockwise"></i> {m.audio_embed_export_reset()}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-primary"
                                        onclick={addRow}
                                        disabled={exporting || availableAudioSources.length === 0}>
                                        <i class="bi bi-plus-lg"></i> {m.audio_embed_export_add_track()}
                                    </button>
                                </div>
                            </div>
                            <div class="card-body">
                                {#if availableAudioSources.length === 0}
                                    <div class="alert alert-warning mb-0 small">
                                        <i class="bi bi-exclamation-triangle"></i> {m.audio_embed_export_no_streams_available()}
                                    </div>
                                {:else if outputRows.length === 0}
                                    <div class="alert alert-warning mb-0 small">
                                        <i class="bi bi-exclamation-triangle"></i> {m.audio_embed_export_no_audio_warning()}
                                    </div>
                                {:else}
                                    <div class="table-responsive">
                                        <table class="table table-sm align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th style="width: 60px;">#</th>
                                                    <th>{m.audio_embed_export_output_source_label()}</th>
                                                    <th style="width: 110px;">{m.audio_embed_export_output_language_label()}</th>
                                                    <th style="width: 140px;" class="text-end">&nbsp;</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {#each outputRows as row, rowIndex (row.id)}
                                                    <tr>
                                                        <td class="text-muted">
                                                            {m.audio_embed_export_output_track_index({ index: rowIndex + 1 })}
                                                        </td>
                                                        <td>
                                                            <select
                                                                class="form-select form-select-sm"
                                                                value={sourceKey(row)}
                                                                onchange={(e) => updateRowSource(rowIndex, e.currentTarget.value)}
                                                                disabled={exporting}>
                                                                {#each availableAudioSources as src (`${src.sourceInput}-${src.streamIdx}`)}
                                                                    <option value={`${src.sourceInput}:${src.streamIdx}`}>{src.label}</option>
                                                                {/each}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                class="form-control form-control-sm"
                                                                bind:value={row.language}
                                                                placeholder={m.audio_embed_export_output_language_placeholder()}
                                                                maxlength="8"
                                                                disabled={exporting} />
                                                        </td>
                                                        <td class="text-end">
                                                            <div class="btn-group btn-group-sm" role="group">
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-outline-secondary"
                                                                    onclick={() => moveRow(rowIndex, -1)}
                                                                    disabled={exporting || rowIndex === 0}
                                                                    aria-label={m.audio_embed_export_output_move_up()}>
                                                                    <i class="bi bi-arrow-up"></i>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-outline-secondary"
                                                                    onclick={() => moveRow(rowIndex, 1)}
                                                                    disabled={exporting || rowIndex === outputRows.length - 1}
                                                                    aria-label={m.audio_embed_export_output_move_down()}>
                                                                    <i class="bi bi-arrow-down"></i>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-outline-danger"
                                                                    onclick={() => removeRow(rowIndex)}
                                                                    disabled={exporting}
                                                                    aria-label={m.audio_embed_export_output_remove()}>
                                                                    <i class="bi bi-trash"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                {/each}
                                            </tbody>
                                        </table>
                                    </div>
                                {/if}
                            </div>
                        </div>

                        <!-- Output options -->
                        <div class="card mb-3" transition:fade={{ duration: 200 }}>
                            <div class="card-header bg-light">
                                <h6 class="mb-0">
                                    <i class="bi bi-gear"></i> {m.audio_embed_export_codec_label()}
                                </h6>
                            </div>
                            <div class="card-body">
                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <label class="form-label" for="audioEmbedCodec">{m.audio_embed_export_codec_label()}</label>
                                        <select
                                            id="audioEmbedCodec"
                                            class="form-select"
                                            bind:value={audioCodecMode}
                                            disabled={exporting}>
                                            {#each AUDIO_CODECS as codec (codec)}
                                                <option value={codec}>{codecLabel(codec)}</option>
                                            {/each}
                                        </select>
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label" for="audioEmbedSuffix">{m.audio_embed_export_suffix_label()}</label>
                                        <input
                                            id="audioEmbedSuffix"
                                            type="text"
                                            class="form-control"
                                            bind:value={filenameSuffix}
                                            disabled={exporting} />
                                        <small class="text-muted">{m.audio_embed_export_suffix_help()}</small>
                                    </div>
                                    <div class="col-12">
                                        <label class="form-label" for="audioEmbedOutputName">{m.audio_embed_export_output_filename_label()}</label>
                                        <input
                                            id="audioEmbedOutputName"
                                            type="text"
                                            class="form-control"
                                            value={outputFileName}
                                            readonly />
                                    </div>
                                </div>
                            </div>
                        </div>
                    {/if}

                </form>
            </div>

            <div class="modal-footer">
                {#if exporting || exportCompleted}
                    <div class="w-100" transition:fade={{ duration: 200 }}>
                        <div class="progress" style="height: 30px;">
                            <div
                                class="progress-bar {exportCompleted ? 'bg-success' : 'bg-primary progress-bar-striped progress-bar-animated'}"
                                role="progressbar"
                                style="width: {progress}%;"
                                aria-valuenow={progress}
                                aria-valuemin="0"
                                aria-valuemax="100">
                                <span class="fw-bold">{progress}%</span>
                            </div>
                        </div>
                        <div class="d-flex justify-content-between align-items-center mt-2">
                            <p class="text-muted mb-0">
                                {#if exportCompleted}
                                    <i class="bi bi-check-circle-fill text-success"></i>
                                {:else}
                                    <i class="bi bi-info-circle"></i>
                                {/if}
                                {statusMsg}
                            </p>
                            {#if exportCompleted}
                                <button
                                    type="button"
                                    class="btn btn-secondary"
                                    onclick={close}>
                                    {m.audio_embed_export_close()}
                                </button>
                            {/if}
                        </div>
                    </div>
                {:else}
                    <button
                        type="button"
                        class="btn btn-secondary"
                        onclick={close}>
                        {m.audio_embed_export_close()}
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        disabled={!canExport || !targetFolderPath}
                        onclick={beginExport}
                        id="audioEmbedExportButton">
                        <i class="bi bi-download"></i> {m.audio_embed_export_export()}
                    </button>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    .table-responsive {
        max-height: 320px;
    }
</style>
